<template>
<div class="tabs">
  <div class="tabs-container">
    <div class="tabs-list">
      <div :class="`tab ${activeTabIndex === i ? 'active' : ''}`" :style="{ maxWidth: `${100 / tabList.length}%` }"
        v-for="(item, i) in tabList" :key="i" @click="onClickTab(i)">
        <div class="tab-name">{{ item }}</div>
        <div class="tab-indicator"></div>
      </div>
    </div>
    <div :class="{
      'tabs-content-wraper': true,
      'first-actived': isFirstTabActive,
      'last-actived': isLastTabActive,
    }">
      <div class="tabs-content-shadow"></div>
      <div class="tabs-content">
        <slot :tabIndex="activeTabIndex"></slot>
      </div>
    </div>
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, ref, withDefaults } from 'vue';

type TabProps = {
  tabList: string[]
};

const props = withDefaults(defineProps<TabProps>(), {
  tabList: () => [],
});
const emits = defineEmits(['clickTab']);

const activeTabIndex = ref(0);
const isFirstTabActive = computed(() => activeTabIndex.value === 0);
const isLastTabActive = computed(() => activeTabIndex.value === props.tabList.length - 1);
const onClickTab = (index: number) => {
  activeTabIndex.value = index;
  emits('clickTab', activeTabIndex.value);
};
</script>

<style lang="less" scoped>
@import (reference) '~@/less/main.less';
.tabs {
  width: 100%;
  &-container {
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
  }
  &-list {
    width: 100%;
    z-index: 2;
    display: flex;
    align-items: flex-end;
    position: relative;
    &::after {
      content: ' ';
      backdrop-filter: blur(20px);
      width: 100%;
      height: 100%;
      position: absolute;
      top: 8/@rem;
      z-index: -1;
      background-color: rgba(255, 255, 255, 0.5);
    }
    .tab {
      height: 72/@rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      position: relative;
      box-shadow: 0px 1px 0px 0px #FFF inset;
      margin-bottom: -8/@rem;
      &-name {
        .font-28-regular(rgba(0, 0, 0, 0.5));
        margin-top: -4/@rem;
        padding: 0 8/@rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      &-indicator {
        position: absolute;
        bottom: 6/@rem;
        width:0;
        height: 8/@rem;
        background-color: #07C160;
      }
      &:first-child {
        border-top-left-radius: 16/@rem;
      }
      &:last-child {
        border-top-right-radius: 16/@rem;
      }
      &::before, &::after {
        content: ' ';
        width: 62/@rem;
        height: 72/@rem;
        position: absolute;
        background-position: 0, 0;
        background-size: 100% 100%;
        opacity: 0;
        pointer-events: none;
      }
      &::before {
        left: -43/@rem;
        background-image: url('https://st.moneydata.hk/res/uploadcdn-static/hkwallet/general/20231228153900/tab-edge.png');
        transform: rotateY(180deg);
      }
      &::after {
        right: -43/@rem;
        background-image: url(https://st.moneydata.hk/res/uploadcdn-static/hkwallet/general/20231228153900/tab-edge.png);
      }
      &:first-child {
        &::before {
          display: none;
        }
      }
      &:last-child {
        &::after {
          display: none;
        }
      }
      &.active {
        background-color: white;
        background-color: white;
        border-top-left-radius: 16/@rem;
        border-top-right-radius: 16/@rem;
        box-shadow: 0px 6/@rem 20/@rem rgba(0, 0, 0, 0.05);
        z-index: 1;
        margin-top: 0;
        margin-bottom: 0;
        .tab-name {
          .font-28-regular(rgba(0, 0, 0, 1));
          position: relative;
        }
        .tab-indicator {
          width: 36/@rem;
          transition: 300ms all ease;
        }
        &::before, &::after {
          opacity: 1;
        }
      }
    }
  }
  &-content-wraper {
    width: 100%;
    position: relative;
    &.first-actived {
      .tabs-content-shadow, .tabs-content {
        border-top-left-radius: 0;
      }
    }
    &.last-actived {
      .tabs-content-shadow, .tabs-content {
        border-top-right-radius: 0;
      }
    }
   .tabs-content-shadow {
      width: 100%;
      height: 100%;
      box-shadow: 0px 6/@rem 20/@rem rgba(0, 0, 0, 0.05);
      border-radius: 16/@rem;
      position: absolute;
    }
    .tabs-content {
      width: 100%;
      height: 100%;
      background-color: white;
      border-radius: 16/@rem;
      z-index: 3;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
